import { default as cloneDeep } from 'lodash/cloneDeep';

import { Optional } from '@vimeo/core/types/generic';

import type { PollManager } from './PollManager';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { trackVenuesAddPollVote } from '../../../../tracking/tracking_venues_engagement';
import { EComposerSessionType } from '../../../../types/composer_session';
import { TFirebasePrescriber, TFirebaseReference } from '../../../../types/firebase';
import { IInteractionApplicationUser } from '../../../../types/interaction_room';
import { EPollState, IPoll, IPollOption } from '../../../../types/interaction_tools';
import { postPageMessage } from '../../../../utils/embed/embed_messaging';
import { EInteractionEmbedSignal } from '../../../../utils/embed/embed_signals';
import { LiveError } from '../../../../utils/error';
import {
  batchedDatabaseUpdate,
  collectObjectBatchPrescribers,
  getRefChild,
  pushRefValue,
  removeRefValue,
  setRefValue,
} from '../../../../utils/firebase/cruds';
import {
  createPollInteractionObject,
  createPollOptionInteractionObject,
} from '../../../../utils/firebase/interaction_objects';
import { range } from '../../../../utils/general/lambda';
import { getAbsoluteNow } from '../../../../utils/general/time';
import { queryInteractionUserSync } from '../../../../utils/store/interaction';
import { ELiveGraphicsSignal } from '../../../signals';

/**
 * Create poll object.
 * Make sure that written object is matching required DB model.
 */
export async function createPoll(manager: PollManager, pollData: IPoll): Promise<void> {
  manager.log.info('Creating poll:', pollData);
  manager.assertIsInitialized();

  const targetRef: TFirebaseReference = await pushRefValue(manager.firebase, manager.pollListRef);
  const poll: IPoll = createPollInteractionObject(
    {
      id: targetRef.key as string,
      question: pollData.question,
      options: pollData.options.map((it) => createPollOptionInteractionObject(it)),
      state: pollData.state,
      createdAt: getAbsoluteNow(),
    },
    { isDbModel: true },
  );

  await setRefValue(manager.firebase, targetRef, {
    ...poll,
    createdAt: manager.firebase.database.serverTimestamp(),
  });

  manager.emitSignal({ type: ELiveGraphicsSignal.POLL_OBJECT_UPDATED, data: poll });
}

/**
 * Delete poll object from interaction tools collection.
 */
export async function deletePoll(manager: PollManager, pollId: string): Promise<void> {
  manager.log.info('Removing poll:', pollId);
  manager.assertIsInitialized();

  await removeRefValue(
    manager.firebase,
    getRefChild(manager.firebase, manager.pollListRef, pollId),
  );

  manager.emitSignal({ type: ELiveGraphicsSignal.POLL_OBJECT_REMOVED, data: pollId });
}

/**
 * Update poll object.
 * Make sure that written object is matching required DB model.
 * Update only fields that are allowed to be updated and do not overwrite nested objects with side changes.
 */
export async function updatePoll(
  manager: PollManager,
  pollId: string,
  pollData: IPoll,
): Promise<void> {
  manager.log.info('Updating poll:', pollId);
  manager.assertIsInitialized();

  const { activePollId } = manager.context;
  const pollRef: TFirebaseReference = getRefChild(manager.firebase, manager.pollListRef, pollId);
  const existingPoll: IPoll = manager.assertPollExists(pollId);

  /**
   * Generate and validate DB model representation of poll object before writing DB update.
   */
  const nextPoll: IPoll = createPollInteractionObject(
    {
      ...pollData,
      updatedAt: getAbsoluteNow(),
    },
    { isDbModel: true },
  );

  /**
   * Collect poll change batches.
   * Include only fields that can be updated and handle nesting of collections.
   */
  const batches: Array<TFirebasePrescriber> = collectObjectBatchPrescribers({
    firebase: manager.firebase,
    ref: pollRef,
    object: { ...nextPoll, updatedAt: manager.firebase.database.serverTimestamp() },
    keys: ['state', 'question', 'updatedAt'],
  });

  /**
   * Collect poll options batches based on old/new poll differences.
   */
  range(interactionToolsConfig.POLLS.MAX_OPTION_COUNT).forEach((idx) => {
    const optionRef: TFirebaseReference = getRefChild(manager.firebase, pollRef, `options/${idx}`);
    const newOption: Optional<IPollOption> = nextPoll.options[idx];
    const oldOption: Optional<IPollOption> = existingPoll.options[idx];

    /**
     * Index is not used, skip check of following range.
     */
    if (!oldOption && !newOption) {
      return;
    }

    /**
     * If new poll just does not have such option that existed before, remove it.
     */
    if (!newOption) {
      return batches.push([optionRef, null]);
    }

    /**
     * Create / replace option or just do plain batch update when ID is the same.
     */
    if (!oldOption || newOption.id !== oldOption.id) {
      return batches.push([optionRef, newOption]);
    } else {
      return batches.push(
        ...collectObjectBatchPrescribers({
          firebase: manager.firebase,
          ref: optionRef,
          keys: ['correct', 'text'],
          object: newOption,
        }),
      );
    }
  });

  /**
   * If archiving poll and trying to update it, also make sure it is inactive.
   */
  if (existingPoll.id === activePollId && nextPoll.state === EPollState.ARCHIVED) {
    await manager.setActivePoll(null);
  }

  /**
   * Update required poll fields in a single batched request collected from model changes.
   */
  await batchedDatabaseUpdate(manager.firebase, manager.rootRef, batches);

  manager.emitSignal({ type: ELiveGraphicsSignal.POLL_OBJECT_UPDATED, data: pollData });
}

/**
 * Set provided poll as active and show it for participants in chat.
 */
export async function setActivePoll(manager: PollManager, pollId: Optional<string>): Promise<void> {
  manager.log.info('Setting active poll:', pollId);
  manager.assertIsInitialized();

  await setRefValue(manager.firebase, manager.pollActiveMetaRef, pollId);
}

/**
 * Remove active poll from selection.
 */
export async function removeActivePoll(manager: PollManager): Promise<void> {
  manager.log.info('Removing active poll');

  return setActivePoll(manager, null);
}

/**
 * Vote for poll option.
 */
export async function voteForPollOption(
  manager: PollManager,
  pollId: string,
  optionIndex: number,
): Promise<void> {
  manager.log.info('Voting for poll option:', pollId, optionIndex);
  manager.assertIsInitialized();
  manager.assertPollExists(pollId);

  const interactionUser: Optional<IInteractionApplicationUser> = queryInteractionUserSync(manager);
  const userId: string = String(interactionUser?.id);

  /**
   * todo: Also check that poll index is within present poll options?
   */
  if (!userId) {
    throw new LiveError('Auth required to vote for poll.');
  } else if (optionIndex < 0 || optionIndex > interactionToolsConfig.POLLS.MAX_OPTION_COUNT) {
    throw new LiveError('Option index out of bounds.');
  }

  /**
   * Mark yourself as voted for poll option using nested votes collection.
   */
  await setRefValue(
    manager.firebase,
    getRefChild(
      manager.firebase,
      manager.pollListRef,
      `${pollId}/options/${optionIndex}/votes/${userId}`,
    ),
    {
      userId: userId,
    },
  );

  if (manager.composerSessionType === EComposerSessionType.VENUE) {
    trackVenuesAddPollVote(
      pollId,
      String(manager.context.polls[pollId].options[optionIndex].id),
      manager.context.polls[pollId].options[optionIndex].correct,
    );
  }

  // Track action.
  postPageMessage(
    EInteractionEmbedSignal.POLL_OPTION_VOTED,
    {
      poll: cloneDeep(manager.context.polls[pollId]),
      optionIndex,
    },
    true,
  );
}
